import { useEffect, useMemo, useRef } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

/**
 * A CONSULTA DA FILA NOS 3 HORÁRIOS (Douglas 30/09/2026).
 *
 * "eu preciso que tenha log em todos os pedidos pelas triagens... e me dê a
 *  solução no contador no topo da triagem marcando se ela consultou nos 3
 *  horários: manhã, meio-dia, final do dia."
 *
 * O topo mostra, para os pedidos que estão NA TELA, quantos foram consultados
 * em cada janela de hoje. Consultar = apertar um BOTÃO da linha: o Manter
 * (consultei e nada mudou) ou o botão da mudança. A régua mora no backend
 * (`services/triagem/consultas-triagem.service.ts`); aqui só se desenha.
 */

type EstadoJanela = 'por_vir' | 'agora' | 'feita' | 'parcial' | 'perdida' | 'sem_fila';

interface JanelaConsulta {
  chave: string; rotulo: string; ini: string; fim: string; estado: EstadoJanela;
  total: number; conferidos: number; atualizados: number; mantidos: number;
  pessoas: Array<{ nome: string; pedidos: number }>;
  aberturas: Array<{ nome: string; hora: string }>;
}

interface ConsultaPedido { hora: string; nome: string; tipo: 'manteve' | 'atualizou'; janelas: string[] }

interface PlacarConsultas {
  dia: string;
  janelas: JanelaConsulta[];
  pedidos: Record<string, ConsultaPedido>;
}

async function requisitar<T>(path: string, body: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/triagens/${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível registrar a consulta.');
  return corpo.data as T;
}

export function useConsultasTriagem(chave: string | null, pedidoIds: string[], segmento: string | undefined, carregando: boolean, ativo: boolean) {
  const queryClient = useQueryClient();
  const ids = useMemo(() => [...new Set(pedidoIds)].sort(), [pedidoIds]);
  const chaveIds = ids.join(',');
  const habilitado = !!chave && ativo;

  const placar = useQuery({
    queryKey: ['triagem-consultas', chave, chaveIds],
    enabled: habilitado,
    staleTime: 30_000,
    refetchInterval: 60_000,
    // A linha que sai da fila muda a chave; o placar fica na tela até o novo chegar.
    placeholderData: keepPreviousData,
    queryFn: () => requisitar<PlacarConsultas>(`${chave}/placar`, { pedido_ids: ids }),
  });

  // Abrir a fila fica registrado uma vez por abertura (o backend ainda junta
  // as reaberturas de 10 min). Espera a lista carregar para gravar quantos
  // pedidos a pessoa tinha na frente.
  const abertura = useRef<string | null>(null);
  useEffect(() => {
    if (!habilitado || carregando) return;
    const marca = `${chave}|${segmento ?? ''}`;
    if (abertura.current === marca) return;
    abertura.current = marca;
    requisitar(`${chave}/abertura`, { pedidos: ids.length, segmento: segmento ?? null })
      .then(() => queryClient.invalidateQueries({ queryKey: ['triagem-consultas', chave] }))
      .catch((e) => console.error('[triagem] abertura não registrada', e));
  }, [habilitado, carregando, chave, segmento, ids.length, queryClient]);

  return habilitado ? placar.data ?? null : null;
}

const DOT: Record<EstadoJanela, string> = {
  feita: 'bg-[#8FE3A8]',
  parcial: 'bg-[#FF9A9A]',
  perdida: 'bg-[#FF9A9A]',
  agora: 'border-[1.5px] border-white bg-transparent',
  por_vir: 'bg-white/25',
  sem_fila: 'bg-white/25',
};

function explicar(j: JanelaConsulta): string {
  const linhas = [`${j.rotulo} · ${j.ini} às ${j.fim}`];
  if (j.estado === 'por_vir') linhas.push('A janela ainda não começou.');
  else if (j.estado === 'sem_fila') linhas.push('Não havia pedido esperando nesta fila.');
  else {
    linhas.push(`${j.conferidos} de ${j.total} pedido${j.total === 1 ? '' : 's'} consultado${j.total === 1 ? '' : 's'}${j.estado === 'agora' ? ' até agora' : ''}.`);
    linhas.push(`${j.atualizados} atualizado${j.atualizados === 1 ? '' : 's'} · ${j.mantidos} mantido${j.mantidos === 1 ? '' : 's'} · falta${j.total - j.conferidos === 1 ? '' : 'm'} ${Math.max(0, j.total - j.conferidos)}.`);
  }
  if (j.pessoas.length) linhas.push(`Quem consultou: ${j.pessoas.map(p => `${p.nome} (${p.pedidos})`).join(', ')}.`);
  linhas.push(j.aberturas.length ? `Abriu a fila: ${j.aberturas.map(a => `${a.nome} às ${a.hora}`).join(', ')}.` : 'Ninguém abriu esta fila na janela.');
  linhas.push('Conta como consulta: o Manter (consultei, nada mudou) ou o botão da mudança.');
  return linhas.join('\n');
}

/** As duas voltas no hero vinho: dot + "Volta da manhã 12/40". O detalhe fica no hover. */
export function PlacarConsultasTriagem({ placar, pill }: { placar: PlacarConsultas | null; pill: string }) {
  if (!placar) return null;
  return (
    <div className="relative flex flex-shrink-0 items-center gap-1.5">
      <span className="text-[9px] font-bold uppercase tracking-wider text-white/60">Consulta hoje</span>
      {placar.janelas.map(j => (
        <span key={j.chave} className={pill} title={explicar(j)}>
          <span className={cn('h-[7px] w-[7px] flex-shrink-0 rounded-full', DOT[j.estado])} />
          {j.rotulo}
          {j.estado === 'por_vir' ? (
            <span className="tabular-nums text-white/60">{j.ini}</span>
          ) : j.estado === 'sem_fila' ? (
            <span className="text-white/60">sem fila</span>
          ) : (
            <span className="font-display text-[11px] font-bold tabular-nums text-white">{j.conferidos}/{j.total}</span>
          )}
        </span>
      ))}
    </div>
  );
}
